'use client';

import { useSearchParams } from 'next/navigation';

import { isMobileUtMode } from '@/shared-survey';
import {
  SurveyThemeProvider,
  supportsDisplayMedia,
  useIsDesktop,
} from '@/shared-utilities';
import {
  InitiateSurveyV3Mutation,
  ProjectStatus,
  type GetProjectQuery,
} from '@/survey-graphql';

import ProjectExpired from './project-response-expired';
import { MIN_USABILITY_VIEWPORT_HEIGHT } from './project-response.consts';
import ProjectUsabilityHandsFreeContent from './project-usability-handsfree-content';
import ProjectUsabilityResponseUnsupportedBrowser from './project-usability-response-unsupported-browser';

interface Props {
  project: GetProjectQuery['project'];
  orgSettings?: GetProjectQuery['projectOrgSettings'];
  firstQuestion: InitiateSurveyV3Mutation['initiateSurveyV3']['firstQuestion'];
  sessionId: string;
}

export default function ProjectUsabilityResponseHandsFree({
  project,
  orgSettings,
  sessionId,
  firstQuestion,
}: Props) {
  const searchParams = useSearchParams();

  const { mounted } = useIsDesktop(1100, MIN_USABILITY_VIEWPORT_HEIGHT);

  const isMobileMode = isMobileUtMode(searchParams);
  const isPublished = project?.status === ProjectStatus.Published;

  const themeValue = {
    themeSettings: project?.themeSettings,
    orgSettings: orgSettings ?? undefined,
    forceSystemDefaults: true as const,
  };

  if (!isPublished) {
    return <ProjectExpired showHomePageButton showPoweredBy />;
  }

  if (!mounted) {
    return (
      <div className='flex h-screen w-screen items-center justify-center bg-neutral-950' />
    );
  }

  if (!supportsDisplayMedia()) {
    return (
      <SurveyThemeProvider value={themeValue}>
        <ProjectUsabilityResponseUnsupportedBrowser />
      </SurveyThemeProvider>
    );
  }

  return (
    <ProjectUsabilityHandsFreeContent
      project={project}
      orgSettings={orgSettings}
      sessionId={sessionId}
      firstQuestion={firstQuestion}
      themeValue={themeValue}
      isMobileMode={isMobileMode}
    />
  );
}
